import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ELEGANT_GOLD = '#C9A961';

const FormSelector = ({ recordType, recordId, onFormAdded }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    if (dialogOpen) {
      fetchTemplates();
    }
  }, [dialogOpen]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/forms/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load form templates');
    }
  };

  const handleAddForm = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a form template');
      return;
    }

    setLoading(true);
    try {
      await api.post('/forms/attach', {
        record_type: recordType,
        record_id: recordId,
        template_id: selectedTemplate
      });
      
      toast.success('Form added successfully!');
      setDialogOpen(false);
      setSelectedTemplate('');
      if (onFormAdded) onFormAdded();
    } catch (error) {
      console.error('Failed to add form:', error);
      toast.error('Failed to add form');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(templates.map(t => t.category))];

  // Filter templates by category
  const filteredTemplates = categoryFilter === 'all' 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);

  // Group templates by category for display
  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {});

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all">
          <Plus className="h-4 w-4 mr-2" />
          Add Form
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-black/95 border-2 border-[#C9A961] backdrop-blur-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#C9A961] flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Add Form to Record
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Category Filter */}
          <div className="space-y-2">
            <label className="text-[#C9A961] font-semibold text-sm">Filter by Category</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-dark-card border-[#C9A961]/30">
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat} className="text-white hover:bg-[#C9A961]/20">
                    {cat === 'all' ? 'All Categories' : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Template Selection */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
              <div key={category} className="space-y-2">
                <h4 className="text-lg font-bold text-[#C9A961] border-b border-[#C9A961]/30 pb-2">
                  {category}
                </h4>
                <div className="space-y-2">
                  {categoryTemplates.map((template) => (
                    <div
                      key={template.template_id}
                      onClick={() => setSelectedTemplate(template.template_id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTemplate === template.template_id
                          ? 'border-[#C9A961] bg-[#C9A961]/10'
                          : 'border-[#C9A961]/20 hover:border-[#C9A961]/50 bg-black/40'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h5 className="text-white font-semibold flex items-center gap-2">
                            {template.is_predefined && (
                              <Sparkles className="h-4 w-4 text-[#C9A961]" />
                            )}
                            {template.template_name}
                          </h5>
                          {template.description && (
                            <p className="text-gray-400 text-sm mt-1">{template.description}</p>
                          )}
                          <p className="text-gray-500 text-xs mt-2">
                            {template.fields.length} fields
                          </p>
                        </div>
                        {selectedTemplate === template.template_id && (
                          <div className="text-[#C9A961]">
                            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-[#C9A961]/30 mx-auto mb-4" />
              <p className="text-gray-400">No templates found in this category</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-[#C9A961]/20">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddForm}
              disabled={!selectedTemplate || loading}
              className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all"
            >
              {loading ? 'Adding...' : 'Add Form'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormSelector;
